import { useState } from "react";
import {
  ChevronRight,
  ClockFading,
  NetworkIcon,
  Paintbrush,
  Router,
  SettingsIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { preventDefault } from "@/lib/utils";
import { NetworkSettings } from "./settings/network";
import { LifetimeSettings } from "./settings/lifetime";
import { AppearanceSettings } from "./settings/appearance";
import { NodeSettings } from "./settings/node";
import { twMerge } from "tailwind-merge";

const data = {
  nav: [
    { name: "Appearance", icon: Paintbrush, value: "appearance" },
    { name: "Transaction Lifetime", shortName: "Txn Lifetime", icon: ClockFading, value: "lifetime" },
    { name: "Switch Network", shortName: "Network", icon: NetworkIcon, value: "network" },
    { name: "Node Settings", shortName: "Node", icon: Router, value: "node" },
  ],
};

export function SettingsDialog({ variant, className }: { variant?: "outline", className?: string }) {
  const [view, setView] = useState("appearance");
  const [open, setOpen] = useState(false);
  const activeData = data.nav.find(({ value: v }) => v === view);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={ variant ?? "ghost"} size="icon" className={className}>
          <SettingsIcon />
        </Button>
      </DialogTrigger>
      <DialogContent className="overflow-hidden p-0 max-w-[100vw] md:max-h-[500px] md:max-w-[700px] lg:max-w-[800px]">
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Customize your settings here.
        </DialogDescription>
        <SidebarProvider className="items-start flex-grow flex-col md:flex-row max-w-[100vw]">
          <Sidebar collapsible="none" className="hidden md:flex">
            <SidebarContent>
              <div className="flex justify-center p-6 pb-2">
                <SettingsIcon />
              </div>{" "}
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {data.nav.map((item) => (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={item.value === view}
                        >
                          <a
                            href="#"
                            onClick={(e: any) => {
                              setView(item.value);
                              preventDefault(e);
                            }}
                          >
                            <item.icon />
                            <span>{item.name}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>

          <main className="flex self-stretch max-w-[100vw] flex-1 flex-col overflow-hidden">
            <div className="p-4 pt-5 pb-1">
              <h2 className="pl-2 flex items-center gap-2"><SettingsIcon className="block md:hidden" /> <ChevronRight size={20} className="block md:hidden" /> {activeData?.name}</h2>
              <div className="flex flex-1 h-[calc(100svh-200px)] md:h-[400px] flex-col mt-5 gap-3 overflow-y-auto px-2">
                {view === "lifetime" ? <LifetimeSettings /> : null}
                {view === "network" ? <NetworkSettings /> : null}
                {view === "appearance" ? <AppearanceSettings /> : null}
                {view === "node" ? <NodeSettings /> : null}
              </div>
            </div>
          </main>
          <div className="flex md:hidden w-full max-w-[100vw] overflow-scroll justify-center items-center bg-sidebar">
            {data.nav.map((item, i) => (
              <Button
                key={`btn-${i}`}
                variant={"ghost"}
                className={twMerge("flex flex-col rounded-none justify-around pt-4 pb-3 items-center h-24 w-28 [&_svg]:size-7 relative", item.value === view ? "bg-sidebar-accent" : "")}
                onClick={(e: any) => {
                  setView(item.value);
                  preventDefault(e);
                }}
              >
                <item.icon />
                <div className="">{item.shortName ?? item.name}</div>
              </Button>
            ))}
          </div>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  );
}
